import React, { useEffect, useState } from "react";
import GameCityLoading from "components/Shared/GameCityLoading/GameCityLoading";
import { getCachedBillboardUrl, refreshLoadingConfig } from "services/loadingConfig";
import styles from "./PreloaderStyles.module.scss";

// Keep the branded screen up briefly even on fast loads so the billboard
// animation reads and the site→game transition doesn't flash.
const MIN_SHOW_MS = 2500;

// On a first-ever visit (no cached billboard) the remote billboard may still
// swap in, but only if it has fetched AND rendered-preloaded this soon after
// mount — late swaps would flash mid-display.
const BILLBOARD_SWAP_WINDOW_MS = 1200;

/**
 * App-boot asset preloader.
 *
 * Renders the shared GameCityLoading scene as an opaque full-viewport overlay
 * while images and fonts fetch in parallel. Calls `onComplete` once every
 * asset has either loaded or failed (errors don't block), then holds until
 * MIN_SHOW_MS and fades out over the live app.
 *
 * **Mounted by `App.js`** (z-index 2000). `onComplete` fires before the fade,
 * so App mounts the Home/Client routes underneath and the preloader fades out
 * over live content.
 */
export default function Preloader({ imagesToLoad, fontsToLoad, onComplete }) {
    const [fading, setFading] = useState(false);
    const [done, setDone] = useState(false);
    const [mountedAt] = useState(() => Date.now());
    // Admin-configured billboard: cached value renders from the first frame;
    // refreshLoadingConfig keeps the cache warm for the next loading screen.
    const [billboardSrc, setBillboardSrc] = useState(() => getCachedBillboardUrl());

    useEffect(() => {
        let cancelled = false;
        const hadCache = !!getCachedBillboardUrl();
        refreshLoadingConfig().then((url) => {
            if (cancelled || !url || hadCache) return;
            const img = new Image();
            img.onload = () => {
                if (!cancelled && Date.now() - mountedAt <= BILLBOARD_SWAP_WINDOW_MS) setBillboardSrc(url);
            };
            img.src = url;
        });
        return () => { cancelled = true; };
    }, [mountedAt]);

    useEffect(() => {
        let cancelled = false;
        const imgList = Array.isArray(imagesToLoad) ? imagesToLoad : [];
        const fontList = Array.isArray(fontsToLoad) ? fontsToLoad : [];
        const finish = () => {
            if (cancelled) return;
            onComplete?.();
            const holdLeft = Math.max(0, MIN_SHOW_MS - (Date.now() - mountedAt));
            setTimeout(() => !cancelled && setFading(true), holdLeft);
        };
        if (imgList.length + fontList.length === 0) { finish(); return; }
        const loadImage = src =>
            new Promise(res => { const img = new Image(); img.onload = img.onerror = () => res(); img.src = src; });
        const loadFont = font =>
            new Promise(res => {
                if (!window.FontFace) { res(); return; }
                const fontFace = new FontFace(font.font, `url(${font.path})`);
                fontFace.load().then(
                    () => { document.fonts.add(fontFace); res(); },
                    () => res()
                );
            });
        // onComplete fires before the fade so the app mounts underneath and the
        // preloader fades out over live content.
        Promise.all([...imgList.map(loadImage), ...fontList.map(loadFont)]).then(finish);
        return () => { cancelled = true; };
    }, [imagesToLoad, fontsToLoad, onComplete, mountedAt]);

    useEffect(() => {
        if (!fading) return;
        const t = setTimeout(() => setDone(true), 450); // 400ms fade + buffer
        return () => clearTimeout(t);
    }, [fading]);

    if (done) return null;

    return (
        <div className={`${styles.preloader} ${fading ? styles.fadeOut : ""}`} aria-busy="true" role="status">
            <GameCityLoading text="Loading" billboardSrc={billboardSrc || undefined} />
        </div>
    );
}
